import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import AppImage from '../../../components/AppImage';

const CustomerReviews = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [itemsPerView, setItemsPerView] = useState(2);
  const [reviews, setReviews] = useState([]);

  const defaultReviews = [
  {
    name: 'Deepak Arora',
    avatar: "https://images.unsplash.com/photo-1708334304775-3e68301e3526",
    alt: 'Professional man with glasses wearing blue shirt',
    rating: 5,
    review: 'Best outstation Taxi service in Chandigarh. Drivers are very professional. They maintain their vehicles very well. I have been using their services for the last 3 years and never faced any issue. Highly recommended for outstation trips.'
  },
  {
    name: 'Sunil Kumar',
    avatar: "https://images.unsplash.com/photo-1714827250733-bcd27ef56269",
    alt: 'Smiling man with family in casual attire',
    rating: 5,
    review: 'Best taxi service in Chandigarh, especially our cab driver Mr. Rajveer gave us the best experience. Very polite and helpful throughout the journey. The vehicle was clean and comfortable. Will definitely book again for our next trip.'
  },
  {
    name: 'Priya Sharma',
    avatar: "https://images.unsplash.com/photo-1650603697000-18d771fe3a7e",
    alt: 'Young woman with long hair smiling outdoors',
    rating: 5,
    review: 'Excellent service from Advait Tour and Travels! Booked a Force Urbania for our family trip to Manali. The vehicle was spacious, clean, and the driver was very experienced. Punctual pickup and smooth journey. Worth every penny!'
  },
  {
    name: 'Rajesh Verma',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18023077a-1763293632312.png",
    alt: 'Businessman in formal attire with confident expression',
    rating: 5,
    review: 'Used Advait Tour and Travels for corporate travel multiple times. Always on time, professional drivers, and well-maintained vehicles. Their customer service is top-notch. Highly recommend for both personal and business travel needs.'
  },
  {
    name: 'Anjali Mehta',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4ba176d-1763299448537.png",
    alt: 'Professional woman with short hair in business casual',
    rating: 5,
    review: 'Amazing experience with Advait Tour and Travels! Booked an Innova Crysta for Shimla trip. Driver was courteous, vehicle was spotless, and the pricing was very reasonable. The booking process was smooth and hassle-free. Will use again!'
  },
  {
    name: 'Vikram Singh',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16a483fa2-1763296945912.png",
    alt: 'Young man with beard wearing casual shirt outdoors',
    rating: 5,
    review: 'Fantastic service! Used their tempo traveller for a group trip to Dharamshala. Spacious, comfortable, and the driver knew all the best routes. Great value for money. Advait Tour and Travels has become our go-to choice for all trips.'
  }];

  // Load reviews from localStorage on mount
  useEffect(() => {
    const loadReviews = () => {
      const savedReviews = localStorage.getItem('advaitTravelsReviews');
      if (savedReviews) {
        try {
          const parsedReviews = JSON.parse(savedReviews);
          // Get user-submitted reviews (those with image property containing ui-avatars)
          const userReviews = parsedReviews?.filter((review) => review?.image?.includes('ui-avatars.com'));
          // Convert user reviews to homepage format
          const formattedUserReviews = userReviews?.map((review) => ({
            name: review?.name,
            avatar: review?.image,
            alt: `${review?.name} profile picture`,
            rating: review?.rating,
            review: review?.review
          }));
          // Merge user reviews with default reviews (user reviews first)
          setReviews([...formattedUserReviews, ...defaultReviews]);
        } catch (error) {
          console.error('Error loading reviews:', error);
          setReviews(defaultReviews);
        }
      } else {
        setReviews(defaultReviews);
      }
    };

    loadReviews();
  }, []);

  // Listen for storage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e?.key === 'advaitTravelsReviews' && e?.newValue) {
        try {
          const parsedReviews = JSON.parse(e?.newValue);
          // Get user-submitted reviews (those with image property containing ui-avatars)
          const userReviews = parsedReviews?.filter((review) => review?.image?.includes('ui-avatars.com'));
          // Convert user reviews to homepage format
          const formattedUserReviews = userReviews?.map((review) => ({
            name: review?.name,
            avatar: review?.image,
            alt: `${review?.name} profile picture`,
            rating: review?.rating,
            review: review?.review
          }));
          // Merge user reviews with default reviews (user reviews first)
          setReviews([...formattedUserReviews, ...defaultReviews]);
        } catch (error) {
          console.error('Error parsing storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Handle responsive items per view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerView(1); // Mobile: 1 slide
      } else {
        setItemsPerView(2); // Tablet/Desktop: 2 slides
      }
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, reviews?.length - itemsPerView);

  // Reset currentIndex if it exceeds maxIndex when itemsPerView changes
  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [maxIndex, currentIndex]);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => prev >= maxIndex ? 0 : prev + 1);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, maxIndex]);

  const handlePrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => prev <= 0 ? maxIndex : prev - 1);
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => prev >= maxIndex ? 0 : prev + 1);
  };

  return (
    <section className="py-16 bg-gradient-to-b from-white to-blue-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-4">
          <h2 className="text-4xl md:text-5xl font-bold text-orange-500 mb-2">
            Advait Tour and Travels Customer Reviews
          </h2>
          <h3 className="text-2xl md:text-3xl font-semibold text-orange-500 mb-6">
            Real Experiences, Real Satisfaction
          </h3>
          <p className="text-gray-700 text-base max-w-5xl mx-auto leading-relaxed">
            Curious about the Advait Tour and Travels experience? Hear it straight from our passengers. Discover genuine reviews that showcase the satisfaction, comfort, and reliability they've found in our service. Each ride is more than a journey; it's a testament to our commitment to excellence. Join the chorus of happy travelers who have chosen Advait Tour and Travels for their unforgettable rides.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative mt-12">
          {/* Navigation Arrows */}
          <button
            onClick={handlePrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110"
            aria-label="Previous reviews">

            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110"
            aria-label="Next reviews">

            <ChevronRight className="w-6 h-6 text-gray-700" />
          </button>

          {/* Reviews Container */}
          <div className="overflow-hidden mx-12">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`
              }}>

              {reviews?.map((review, index) =>
              <div
                key={index}
                className="flex-shrink-0 px-4"
                style={{ width: `${100 / itemsPerView}%` }}>

                  <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-8 h-full">
                    {/* Customer Info */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="relative">
                        <AppImage
                        src={review?.avatar}
                        alt={review?.alt}
                        className="w-16 h-16 rounded-full object-cover border-2 border-orange-500" />

                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-800">
                          {review?.name}
                        </h4>
                        <div className="flex gap-1 mt-1">
                          {Array.from({ length: review?.rating })?.map((_, i) =>
                        <Star
                          key={i}
                          className="w-4 h-4 fill-yellow-400 text-yellow-400" />

                        )}
                        </div>
                      </div>
                    </div>

                    {/* Review Text */}
                    <p className="text-gray-600 leading-relaxed text-base">
                      {review?.review}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Indicators */}
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: maxIndex + 1 })?.map((_, index) =>
          <button
            key={index}
            onClick={() => {
              setIsAutoPlaying(false);
              setCurrentIndex(index);
            }}
            className={`h-2 rounded-full transition-all duration-300 ${
            currentIndex === index ? 'w-8 bg-orange-500' : 'w-2 bg-gray-300'}`
            }
            aria-label={`Go to review ${index + 1}`} />

          )}
        </div>
      </div>
    </section>);

};

export default CustomerReviews;